/**
 * v4.52 — shared download helper for every canvas download surface (bottom
 * toolbar, floating image toolbar, lightbox, context menu).
 *
 * The old per-surface code fetched CDN images from the BROWSER, which fails
 * CORS (Bunny sends no Access-Control-Allow-Origin) and fell back to a
 * cross-origin `<a download>` — browsers ignore the download attribute there
 * and NAVIGATE instead, so ads opened fullscreen in a tab. The server has no
 * CORS constraints: it fetches the images, builds ads.csv from canonical DB
 * copy (image fields + canvas_ad_copy variations), and streams back ONE
 * same-origin file — a bare image for a single copyless ad, otherwise a zip.
 */
export async function downloadCreatives(
  projectId: number,
  clientIds: string[],
): Promise<{ ok: boolean; filename?: string; error?: string }> {
  if (!projectId || clientIds.length === 0) return { ok: false, error: 'Nothing selected' };
  try {
    const resp = await fetch(`/api/canvas/projects/${projectId}/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientIds }),
    });
    if (!resp.ok) {
      let msg = `Download failed (${resp.status})`;
      try { msg = (await resp.json())?.error ?? msg; } catch { /* keep default */ }
      return { ok: false, error: msg };
    }
    const blob = await resp.blob();
    const cd = resp.headers.get('Content-Disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'admoji-download';
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return { ok: true, filename };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Download failed' };
  }
}
